// components/cierres/ListadoCierresZ.js
import React, { useMemo, useState } from "react";
import { Container, Table, Button } from "react-bootstrap";
import {
  BsChevronLeft,
  BsChevronRight,
  BsArrowUpShort,
  BsArrowDownShort,
} from "react-icons/bs";
import * as XLSX from "xlsx";

const ListadoCierresZ = () => {
  const [cierresRaw, setCierresRaw] = useState([]); // datos crudos del backend
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [cuit, setCuit] = useState("");
  const [puntoVenta, setPuntoVenta] = useState("");
  const [numeroZeta, setNumeroZeta] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [cierresPerPage] = useState(10);

  // sort state
  const [sortBy, setSortBy] = useState("fechaJornada"); // 'fechaJornada' | 'puntoVenta' | 'numeroZeta'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  const apiUrl = process.env.REACT_APP_API_URL;

  const exportToExcel = () => {
    if (sortedCierres.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }
    const dataFormateada = sortedCierres.map((c) => ({
      ID: c.id,
      Fecha: formatearFecha(c.fechaJornada),
      CUIT: c.cuit,
      "Punto Venta": c.puntoVenta,
      "Nro Z": c.numeroZeta,
      "Primer Comp.": c.primerComprobante,
      "Último Comp.": c.ultimoComprobante,
      Emitidos: c.cantidadEmitidos,
      Cancelados: c.cantidadCancelados,
      Neto: parseFloat(c.neto),
      "IVA 10.5%": parseFloat(c.iva105),
      "IVA 21%": parseFloat(c.iva21),
      "IVA Total": parseFloat(c.ivaTotal),
      Total: parseFloat(c.total),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataFormateada);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CierresZ");
    XLSX.writeFile(
      workbook,
      `cierresZ_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // SOLO filtra en backend por fechas y CUIT (Pto Vta y Z se filtran en frontend)
  const handleFilter = async () => {
    try {
      const res = await fetch(`${apiUrl}/caja/cierrez/filtrados`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fechaDesde,
          fechaHasta,
          cuit,
          // NO ENVIAR puntoVenta ni numeroZeta
        }),
      });

      if (!res.ok) throw new Error("Error al obtener los cierres Z");
      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        setCierresRaw([]);
        alert("No hay cierres para la búsqueda solicitada.");
      } else {
        setCierresRaw(data);
      }

      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      alert("Error al filtrar cierres Z");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de que desea eliminar este cierre Z?"))
      return;
    try {
      const res = await fetch(`${apiUrl}/caja/cierrez/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Error al eliminar");
      setCierresRaw((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
      alert("Error al eliminar cierre Z");
    }
  };

  // ---- helpers ----

  const formatearFecha = (fecha) => {
  if (!fecha) return "";

  const fechaLimpia = String(fecha).slice(0, 10);
  const [anio, mes, dia] = fechaLimpia.split("-");

  if (!anio || !mes || !dia) {
    return fecha;
  }

  return `${dia}/${mes}/${anio}`;
};

  const formatearNumero = (valor) =>
    new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(valor) || 0);

  const parsePV = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const parseNZ = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const parseFecha = (v) => {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };

  // 1) FILTRADO EN FRONTEND (puntoVenta / numeroZeta)
  const filteredCierres = useMemo(() => {
    const pv = puntoVenta.trim() === "" ? null : parsePV(puntoVenta);
    const nz = numeroZeta.trim() === "" ? null : parseNZ(numeroZeta);

    return cierresRaw.filter((row) => {
      const rowPV = parsePV(row.puntoVenta);
      const rowNZ = parseNZ(row.numeroZeta);

      const matchPV = pv === null ? true : rowPV === pv;
      const matchNZ = nz === null ? true : rowNZ === nz;

      return matchPV && matchNZ;
    });
  }, [cierresRaw, puntoVenta, numeroZeta]);

  // 2) ORDENAMIENTO
  const compare = (a, b) => {
    let av;
    let bv;

    if (sortBy === "fechaJornada") {
      av = parseFecha(a.fechaJornada);
      bv = parseFecha(b.fechaJornada);
    } else if (sortBy === "puntoVenta") {
      av = parsePV(a.puntoVenta) ?? -Infinity;
      bv = parsePV(b.puntoVenta) ?? -Infinity;
    } else if (sortBy === "numeroZeta") {
      av = parseNZ(a.numeroZeta) ?? -Infinity;
      bv = parseNZ(b.numeroZeta) ?? -Infinity;
    } else {
      av = a?.[sortBy];
      bv = b?.[sortBy];
    }

    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  };

  const sortedCierres = useMemo(() => {
    const copy = [...filteredCierres];
    copy.sort(compare);
    return copy;
  }, [filteredCierres, sortBy, sortDir]);

  // 3) PAGINACIÓN (sobre filtrados + ordenados)
  const totalPages = Math.ceil(sortedCierres.length / cierresPerPage) || 1;
  const indexOfLast = currentPage * cierresPerPage;
  const indexOfFirst = indexOfLast - cierresPerPage;
  const currentCierres = sortedCierres.slice(indexOfFirst, indexOfLast);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const calcularTotales = () => {
    const totalNeto = sortedCierres.reduce(
      (sum, item) => sum + parseFloat(item.neto || 0),
      0
    );
    const totalIva105 = sortedCierres.reduce(
      (sum, item) => sum + parseFloat(item.iva105 || 0),
      0
    );
    const totalIva21 = sortedCierres.reduce(
      (sum, item) => sum + parseFloat(item.iva21 || 0),
      0
    );
    const totalIvaTotal = sortedCierres.reduce(
      (sum, item) => sum + parseFloat(item.ivaTotal || 0),
      0
    );
    const totalTotal = sortedCierres.reduce(
      (sum, item) => sum + parseFloat(item.total || 0),
      0
    );

    return { totalNeto, totalIva105, totalIva21, totalIvaTotal, totalTotal };
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortDir === "asc" ? (
      <BsArrowUpShort className="ms-1" />
    ) : (
      <BsArrowDownShort className="ms-1" />
    );
  };

  return (
    <Container className="vt-page">
      <h1 className="my-list-title dark-text vt-title">Cierres Z</h1>

      {/* Barra superior: acción principal */}
      <div className="d-flex justify-content-end mb-3">
        <Button variant="success" onClick={exportToExcel} className="vt-btn">
          Exportar a Excel
        </Button>
      </div>

      {/* Filtros */}
      <div className="vt-toolbar mb-3 d-flex flex-wrap align-items-end gap-3">
        <div className="d-inline-block w-auto mx-2">
          <label className="mr-2">Desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="form-control rounded-0 text-center vt-input"
          />
        </div>

        <div className="d-inline-block w-auto mx-2">
          <label className="ml-2 mr-2">Hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="form-control rounded-0 text-center vt-input"
          />
        </div>

        <div className="d-inline-block w-auto mx-2">
          <label className="d-block">CUIT</label>
          <select
            value={cuit}
            onChange={(e) => setCuit(e.target.value)}
            className="form-control vt-input"
            style={{ minWidth: 240 }}
          >
            <option value="">Seleccione CUIT</option>
            <option value="30708490004">30708490004</option>
            <option value="20246050822">20246050822</option>
          </select>
        </div>

        {/* FILTROS FRONTEND */}
        <div className="d-inline-block w-auto mx-2">
          <label className="d-block">Punto de Venta</label>
          <input
            type="number"
            min="0"
            value={puntoVenta}
            onChange={(e) => {
              setPuntoVenta(e.target.value);
              setCurrentPage(1);
            }}
            className="form-control vt-input text-center"
            placeholder="Ej: 5"
            style={{ minWidth: 140 }}
          />
        </div>

        <div className="d-inline-block w-auto mx-2">
          <label className="d-block">Nro Z</label>
          <input
            type="number"
            min="0"
            value={numeroZeta}
            onChange={(e) => {
              setNumeroZeta(e.target.value);
              setCurrentPage(1);
            }}
            className="form-control vt-input text-center"
            placeholder="Ej: 123"
            style={{ minWidth: 140 }}
          />
        </div>

        <div className="d-inline-block mx-2">
          <Button variant="primary" onClick={handleFilter} className="vt-btn">
            Filtrar
          </Button>
        </div>
      </div>

      {/* Tabla */}
      <div className="vt-tablewrap table-responsive">
        <Table striped bordered hover className="mb-2">
          <thead>
            <tr>
              <th className="vt-th-sort">ID</th>

              {/* columnas con ordenamiento */}
              <th
                className="vt-th-sort"
                role="button"
                onClick={() => handleSort("fechaJornada")}
                title="Ordenar por Fecha"
              >
                Fecha Jornada <SortIcon column="fechaJornada" />
              </th>

              <th className="vt-th-sort">CUIT</th>

              <th
                className="vt-th-sort"
                role="button"
                onClick={() => handleSort("puntoVenta")}
                title="Ordenar por Punto de Venta"
              >
                Punto Venta <SortIcon column="puntoVenta" />
              </th>

              <th
                className="vt-th-sort"
                role="button"
                onClick={() => handleSort("numeroZeta")}
                title="Ordenar por Nro Z"
              >
                Nro Z <SortIcon column="numeroZeta" />
              </th>

              <th className="vt-th-sort">Primer Comp.</th>
              <th className="vt-th-sort">Último Comp.</th>
              <th className="vt-th-sort text-end">Emitidos</th>
              <th className="vt-th-sort text-end">Cancelados</th>
              <th className="vt-th-sort text-end">Neto</th>
              <th className="vt-th-sort text-end">IVA 10.5%</th>
              <th className="vt-th-sort text-end">IVA 21%</th>
              <th className="vt-th-sort text-end">IVA Total</th>
              <th className="vt-th-sort text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {currentCierres.map((cierre) => (
              <tr key={cierre.id}>
                <td>{cierre.id}</td>
                <td>{formatearFecha(cierre.fechaJornada)}</td>
                <td>{cierre.cuit}</td>
                <td>{cierre.puntoVenta}</td>
                <td>{cierre.numeroZeta}</td>
                <td>{cierre.primerComprobante}</td>
                <td>{cierre.ultimoComprobante}</td>
                <td className="text-end">{cierre.cantidadEmitidos}</td>
                <td className="text-end">{cierre.cantidadCancelados}</td>
                <td className="text-end">{formatearNumero(cierre.neto)}</td>
                <td className="text-end">{formatearNumero(cierre.iva105)}</td>
                <td className="text-end">{formatearNumero(cierre.iva21)}</td>
                <td className="text-end">{formatearNumero(cierre.ivaTotal)}</td>
                <td className="text-end">{formatearNumero(cierre.total)}</td>
              </tr>
            ))}

            {/* {sortedCierres.length > 0 && (() => {
              const {
                totalNeto,
                totalIva105,
                totalIva21,
                totalIvaTotal,
                totalTotal,
              } = calcularTotales();

              return (
                <tr className="vt-total-row">
                  <td colSpan={9} className="text-end">
                    <strong>Totales:</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatearNumero(totalNeto)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatearNumero(totalIva105)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatearNumero(totalIva21)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatearNumero(totalIvaTotal)}</strong>
                  </td>
                  <td className="text-end">
                    <strong>{formatearNumero(totalTotal)}</strong>
                  </td>
                </tr>
              );
            })()} */}
          </tbody>


        </Table>
      </div>

      {/* SUBTOTALES DE LOS REGISTROS FILTRADOS */}
      {sortedCierres.length > 0 && (() => {

        const {
          totalNeto,
          totalIva105,
          totalIva21,
          totalIvaTotal,
          totalTotal,
        } = calcularTotales();

        return (
          <div className="d-flex justify-content-end mb-3">
            <div className="text-end">

              <div className="fw-bold">
                Neto:{" "}
                {formatearNumero(totalNeto)}
              </div>

              <div className="fw-bold">
                IVA 10.5%:{" "}
                {formatearNumero(totalIva105)}
              </div>

              <div className="fw-bold">
                IVA 21%:{" "}
                {formatearNumero(totalIva21)}
              </div>

              <div className="fw-bold">
                IVA Total:{" "}
                {formatearNumero(totalIvaTotal)}
              </div>

              <div className="fw-bold fs-5 mt-1">
                Total:{" "}
                {formatearNumero(totalTotal)}
              </div>

            </div>
          </div>
        );
      })()}

      {/* Paginación */}
      <div className="d-flex justify-content-center align-items-center vt-pager">
        <Button onClick={prevPage} disabled={currentPage === 1} variant="light">
          <BsChevronLeft />
        </Button>
        <span className="mx-2">
          Página {currentPage} de {totalPages}
        </span>
        <Button
          onClick={nextPage}
          disabled={currentPage === totalPages || totalPages === 0}
          variant="light"
        >
          <BsChevronRight />
        </Button>
      </div>
    </Container>
  );
};

export default ListadoCierresZ;
